import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import QrScanner from 'qr-scanner';
import { toast } from 'react-toastify';
import agentService from '../../services/agentService';

const DEBOUNCE_MS = 3000;

const ScanTicket = () => {
  const location = useLocation();
  const videoRef = useRef(null);
  const scannerRef = useRef(null);
  const lastScannedRef = useRef({ code: null, ts: 0 });

  const [step, setStep] = useState('setup'); // 'setup' | 'scan'
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(location.state?.event_id || '');
  const [operation, setOperation] = useState('checkin');
  const [scanning, setScanning] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [manualQr, setManualQr] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [validateResult, setValidateResult] = useState(null);
  const [eventMenuOpen, setEventMenuOpen] = useState(false);
  const eventMenuRef = useRef(null);
  const successAudioRef = useRef(null);
  const errorAudioRef = useRef(null);

  const selectedEvent = events.find(ev => String(ev.id) === String(selectedEventId));

  // Audio feedback for scan outcomes (files live in frontend/public)
  const playSound = useCallback((success) => {
    try {
      if (!successAudioRef.current) {
        successAudioRef.current = new Audio(`${process.env.PUBLIC_URL}/success.mp3`);
        errorAudioRef.current = new Audio(`${process.env.PUBLIC_URL}/error.mp3`);
      }
      const audio = success ? successAudioRef.current : errorAudioRef.current;
      audio.currentTime = 0;
      audio.play().catch(() => {});
    } catch (_) {}
  }, []);

  // Close the event dropdown when clicking/tapping outside it
  useEffect(() => {
    const handler = (e) => {
      if (eventMenuRef.current && !eventMenuRef.current.contains(e.target)) {
        setEventMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, []);

  useEffect(() => {
    agentService.getAssignedEvents()
      .then((r) => {
        const evs = r.data?.data || [];
        setEvents(evs);
        if (!selectedEventId && evs.length === 1) {
          setSelectedEventId(String(evs[0].id));
        }
      })
      .catch(console.error)
      .finally(() => setLoadingEvents(false));
  }, [selectedEventId]);

  const doScan = useCallback(async (qrcode) => {
    if (!selectedEventId) {
      playSound(false);
      toast.warning('Please select an event first.');
      return;
    }
    const now = Date.now();
    if (qrcode === lastScannedRef.current.code && now - lastScannedRef.current.ts < DEBOUNCE_MS) return;
    lastScannedRef.current = { code: qrcode, ts: now };

    // ── Validate only — look up ticket owner & scan status, no check-in/out ──
    if (operation === 'validate') {
      try {
        setSubmitting(true);
        const res = await agentService.validateTicket({
          qrcode,
          event_id: parseInt(selectedEventId, 10),
        });
        if (res.data.success) {
          playSound(true);
          setValidateResult(res.data.data);
        } else {
          playSound(false);
          toast.error(res.data.message || 'Ticket not found.');
        }
      } catch (err) {
        playSound(false);
        toast.error(err.response?.data?.message || 'Validation failed. Please try again.');
      } finally {
        setSubmitting(false);
      }
      return;
    }

    try {
      setSubmitting(true);
      const res = await agentService.scanTicket({
        qrcode,
        event_id: parseInt(selectedEventId, 10),
        operation,
      });
      const data = res.data;
      const attendee = data.user && (data.user.first_name || data.user.id > 0)
        ? ` — ${[data.user.first_name, data.user.last_name].filter(Boolean).join(' ')}`
        : '';
      if (data.success) {
        playSound(true);
        toast.success(`${data.message}${attendee}`);
      } else {
        playSound(false);
        toast.error(data.message || 'Scan failed. Please try again.');
      }
    } catch (err) {
      playSound(false);
      toast.error(err.response?.data?.message || 'Scan failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [selectedEventId, operation, playSound]);

  // Always call the latest doScan without restarting the camera — changing
  // the operation (check-in/check-out) or event must NOT recreate the scanner
  const doScanRef = useRef(doScan);
  useEffect(() => { doScanRef.current = doScan; }, [doScan]);

  useEffect(() => {
    if (step !== 'scan' || !scanning || !videoRef.current) return;
    const scanner = new QrScanner(
      videoRef.current,
      (r) => doScanRef.current(r.data),
      { returnDetailedScanResult: true, highlightScanRegion: true }
    );
    scannerRef.current = scanner;
    scanner.start().catch((e) => { console.error(e); setScanning(false); });
    return () => { scanner.stop(); scanner.destroy(); scannerRef.current = null; };
  }, [scanning, step]);

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (manualQr.trim()) { doScan(manualQr.trim()); setManualQr(''); }
  };

  const handleContinue = () => {
    if (!selectedEventId) {
      toast.warning('Please select an event first.');
      return;
    }
    setStep('scan');
  };

  // Return to setup — stop the camera so it releases cleanly
  const handleBackToSetup = () => {
    setScanning(false);
    setStep('setup');
  };

  // ── Page header (shared) ───────────────────────────────────────────────────
  const Header = () => (
    <div className="flex items-center gap-3">
      <div className="bg-purple-100 rounded-xl p-2.5">
        <svg className="w-5 h-5 text-purple-700" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
        </svg>
      </div>
      <div>
        <h1 className="text-xl font-bold text-gray-900">Scan Tickets</h1>
        <p className="text-sm text-gray-600">
          {step === 'setup' ? 'Select an event and action to begin' : 'Use camera or enter code manually'}
        </p>
      </div>
    </div>
  );

  // ═══════════════ STEP 1 — Setup ═══════════════
  if (step === 'setup') {
    return (
      <div className="max-w-lg mx-auto space-y-6 pb-10">
        <Header />

        {/* Event selector */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-2">
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">Event</label>
          {loadingEvents ? (
            <div className="h-10 bg-gray-100 rounded-lg animate-pulse" />
          ) : (
            <div className="relative" ref={eventMenuRef}>
              <button
                type="button"
                onClick={() => setEventMenuOpen(o => !o)}
                className="w-full flex items-center justify-between gap-2 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-left focus:outline-none focus:ring-2 focus:ring-purple-500 transition bg-white"
              >
                <span className={selectedEventId ? 'text-gray-900' : 'text-gray-500'}>
                  {selectedEvent?.title || '— Select event —'}
                </span>
                <svg className={`w-4 h-4 text-gray-500 shrink-0 transition-transform ${eventMenuOpen ? 'rotate-180' : ''}`}
                  fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {eventMenuOpen && (
                <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-30 overflow-hidden">
                  {events.length === 0 ? (
                    <p className="px-3 py-2.5 text-sm text-gray-500">No events assigned.</p>
                  ) : events.map((ev) => {
                    const isSelected = String(ev.id) === String(selectedEventId);
                    return (
                      <button
                        key={ev.id}
                        type="button"
                        onClick={() => { setSelectedEventId(String(ev.id)); setEventMenuOpen(false); }}
                        className={`w-full text-left px-3 py-2.5 text-sm transition flex items-center justify-between gap-2 ${
                          isSelected ? 'bg-purple-50 text-purple-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <span className="truncate">{ev.title}</span>
                        {isSelected && (
                          <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Operation toggle */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-1 grid grid-cols-3 gap-1">
          <button
            onClick={() => setOperation('checkin')}
            className={`py-2.5 text-sm font-semibold rounded-lg transition ${
              operation === 'checkin'
                ? 'bg-green-600 text-white shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            ✓ Check In
          </button>
          <button
            onClick={() => setOperation('checkout')}
            className={`py-2.5 text-sm font-semibold rounded-lg transition ${
              operation === 'checkout'
                ? 'bg-red-600 text-white shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            ↩ Check Out
          </button>
          <button
            onClick={() => setOperation('validate')}
            className={`py-2.5 text-sm font-semibold rounded-lg transition ${
              operation === 'validate'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            🔍 Validate
          </button>
        </div>

        {/* Continue */}
        <button
          onClick={handleContinue}
          disabled={!selectedEventId}
          className="w-full text-white font-semibold px-6 py-3 rounded-xl text-sm transition hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          style={{ background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)' }}
        >
          Continue to Scanning
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    );
  }

  // ═══════════════ STEP 2 — Scan ═══════════════
  return (
    <div className="max-w-lg mx-auto space-y-6 pb-10">
      <Header />

      {/* Back */}
      <button
        onClick={handleBackToSetup}
        className="text-sm font-semibold text-purple-700 hover:text-purple-900 flex items-center gap-1.5"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
        Back
      </button>

      {/* Camera scanner */}
      <div className="bg-black rounded-2xl overflow-hidden relative shadow-lg" style={{ minHeight: 460 }}>
        <video ref={videoRef} className="w-full h-full object-cover" style={{ minHeight: 460 }} playsInline muted />
        {!scanning && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 gap-3">
            <div className="border-2 border-white/20 rounded-2xl p-5">
              <svg className="w-10 h-10 text-white/40" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <button
              onClick={() => setScanning(true)}
              className="text-white font-semibold px-6 py-2.5 rounded-xl text-sm transition hover:brightness-110"
              style={{ background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)' }}
            >
              Start Camera
            </button>
          </div>
        )}
        {scanning && (
          <button
            onClick={() => setScanning(false)}
            className="absolute top-3 right-3 bg-black/60 text-white text-xs px-3 py-1.5 rounded-lg hover:bg-black/80 transition"
          >
            Stop
          </button>
        )}
      </div>

      {/* Manual entry */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Manual Entry</label>
        <form onSubmit={handleManualSubmit} className="flex gap-2">
          <input
            type="text"
            placeholder="Enter QR code (e.g. TKT-XXXXXXXXXXXX)"
            value={manualQr}
            onChange={(e) => setManualQr(e.target.value)}
            className="flex-1 border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 transition"
          />
          <button
            type="submit"
            disabled={submitting || !manualQr.trim()}
            className="disabled:opacity-40 text-white text-sm font-semibold px-4 py-2 rounded-lg transition whitespace-nowrap hover:brightness-110"
            style={{ background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)' }}
          >
            {submitting ? '…' : 'Submit'}
          </button>
        </form>
      </div>

      {/* Validate result modal */}
      {validateResult && (() => {
        const { owner, ticket, transactions } = validateResult;
        const fmt = (dt) => new Date(dt).toLocaleString('en-US', {
          year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
        });
        return (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
            onClick={() => setValidateResult(null)}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Status banner */}
              <div className={`px-5 py-4 rounded-t-2xl flex items-center gap-3 ${ticket.is_scanned ? 'bg-green-50' : 'bg-gray-50'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${ticket.is_scanned ? 'bg-green-100' : 'bg-gray-200'}`}>
                  {ticket.is_scanned ? (
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-gray-900">{ticket.label}</p>
                  <p className={`text-xs font-semibold ${ticket.is_scanned ? 'text-green-700' : 'text-gray-500'}`}>
                    {ticket.is_scanned ? 'Already scanned' : 'Not scanned yet'}
                  </p>
                </div>
                <button onClick={() => setValidateResult(null)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none shrink-0">&times;</button>
              </div>

              <div className="p-5 space-y-5">
                {/* Owner */}
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Ticket Owner</p>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex items-center gap-2 text-gray-900">
                      <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                      <span className="font-medium">{[owner.first_name, owner.last_name].filter(Boolean).join(' ') || '—'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                      <span className="truncate">{owner.email || '—'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                      <span dir="ltr">{owner.mobile || '—'}</span>
                    </div>
                  </div>
                </div>

                {/* Scan history */}
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Scan History</p>
                  {transactions.length === 0 ? (
                    <p className="text-sm text-gray-500">This ticket has not been scanned yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {transactions.map((tx, idx) => (
                        <div key={idx} className="flex items-center justify-between gap-2 border border-gray-100 rounded-lg px-3 py-2">
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                              tx.type === 'checkin' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                            }`}>
                              {tx.type === 'checkin' ? 'Check In' : 'Check Out'}
                            </span>
                            <span className="text-xs text-gray-500">by {tx.agent_name}</span>
                          </div>
                          <span className="text-xs text-gray-500 whitespace-nowrap">{fmt(tx.datetime)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

    </div>
  );
};

export default ScanTicket;
